/* eslint-disable component-structure/enforce-component-structure -- Error boundaries must be class components and cannot follow Container/View pattern */
/* eslint-disable functional/no-classes -- Error boundaries must be class components in React */
import * as Sentry from "@sentry/react-native";
import React, { Component, ErrorInfo, ReactNode } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

declare const __DEV__: boolean;

/**
 * Props for AIPlayerSummaryErrorBoundary component
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  isDark?: boolean;
}

/**
 * State for AIPlayerSummaryErrorBoundary component
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error boundary component to prevent AIPlayerSummary crashes from taking down the app
 * Catches any unhandled exceptions and displays a fallback UI
 *
 * This is critical for preventing the black screen crash on mobile devices
 * when UUID generation or SSE connection fails catastrophically
 */
class AIPlayerSummaryErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to Sentry
    console.error("❌ AIPlayerSummary crashed:", error, errorInfo);

    Sentry.captureException(error, {
      tags: {
        component: "AIPlayerSummaryErrorBoundary",
        context: "ai-summary-crash",
      },
      extra: {
        errorInfo: errorInfo.componentStack,
        errorMessage: error.message,
        errorName: error.name,
      },
    });

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render(): React.ReactElement {
    if (this.state.hasError) {
      // Fallback UI when error occurs
      return (
        <Box className="border-border-outline-subtle rounded-lg border bg-surface-subtle p-4">
          <Stack className="gap-3">
            <Text variant="title-sm" className="text-status-error">
              AI Summary Unavailable
            </Text>
            <Text variant="body" className="text-content-secondary">
              We encountered an error loading the AI summary. This has been
              reported and will be fixed soon.
            </Text>
            {__DEV__ && this.state.error && (
              <Text variant="caption" className="text-content-muted">
                {this.state.error.message}
              </Text>
            )}
            <Button
              label="Try Again"
              tone="primary"
              size="sm"
              onPress={this.handleReset}
            />
          </Stack>
        </Box>
      );
    }

    // Normal render - wrap in Fragment for consistent JSX return type
    return <>{this.props.children}</>;
  }
}

/* eslint-enable functional/no-classes -- Re-enable after error boundary class */
/* eslint-enable component-structure/enforce-component-structure -- Re-enable rule after error boundary exception */

export default AIPlayerSummaryErrorBoundary;
